/**
 * src/state/employeeWriteDiff.js
 *
 * Which employee records this client has actually changed since the server
 * last confirmed a shape (ADR-0008 §2.4, EMPA-03).
 *
 * Employees live in dbo.tenant_employees, one row per record with its own
 * ROWVERSION. Per-record versioning removes false conflicts between DIFFERENT
 * employees, but a client resubmitting an untouched-yet-stale copy of employee
 * X is still asking to revert a concurrent edit to X — and will correctly get
 * a 409. So the client must post only what it changed, which is what this
 * computes: `serverState` is the last shape the server confirmed to THIS
 * client, `nextState` is what it is about to save.
 *
 * Deletions are deliberately NOT derived here. Absence from an array is "that
 * client never fetched it" at least as often as it is a deletion, and that
 * ambiguity is GH #3903 (ADR-0002 §3.5). A deletion is issued explicitly, by
 * id, by the action that performed it (usePeopleActions.deleteEmployee).
 */

function fingerprint(employee) {
  try {
    return JSON.stringify(employee);
  } catch {
    // A record that will not serialise cannot be compared; treat it as changed
    // so the write path — not this diff — is the thing that reports the
    // problem.
    return null;
  }
}

/**
 * @param {object|null} serverState the last server-confirmed state, or null
 *   when this client has none yet
 * @param {object|null} nextState the state about to be saved
 * @returns {object[]} the employee records to post to employees-write
 */
export function diffEmployeesForWrite(serverState, nextState) {
  const next = Array.isArray(nextState?.employees) ? nextState.employees : [];
  const withIds = next.filter((e) => e && e.id);
  if (withIds.length === 0) return [];

  const prevList = Array.isArray(serverState?.employees) ? serverState.employees : null;
  // No confirmed shape to diff against — every record counts as changed. New
  // tenants are per-record-authoritative from birth (seedTenantStores writes
  // the marker row at signup), so posting nothing here would leave the first
  // save's employees in the blob only, where nothing reads them.
  if (prevList === null) return withIds;

  const before = new Map();
  for (const e of prevList) {
    if (e && e.id) before.set(e.id, fingerprint(e));
  }

  const changed = [];
  for (const e of withIds) {
    const prev = before.get(e.id);
    if (prev === undefined) {
      changed.push(e); // a create
      continue;
    }
    const now = fingerprint(e);
    if (now === null || now !== prev) changed.push(e);
  }
  return changed;
}
